import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';

const GDPRRights = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Your GDPR Rights | MyBodyToday</title>
        <meta name="description" content="Learn about your data protection rights under GDPR including data access, portability, correction, erasure, and restriction." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Go back"
            >
              <Icon name="ArrowLeft" size={24} strokeWidth={2} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                Your GDPR Rights
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Understanding your data protection rights
              </p>
            </div>
            <div className="flex items-center gap-2">
              <LogoutButton showLabel={false} ariaLabel="Sign out from GDPR rights" />
              <CloseButton
                onClose={() => navigate('/settings-and-privacy')}
                navigateTo="/settings-and-privacy"
                ariaLabel="Close GDPR rights"
              />
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Introduction */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Your Data, Your Rights
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Under the General Data Protection Regulation (GDPR), you have comprehensive rights over your personal data. 
                  MyBodyToday is committed to protecting your privacy and ensuring you can exercise these rights easily.
                </p>
              </div>
            </div>
          </div>

          {/* Right to Access */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Eye" size={20} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Access (Article 15)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to obtain confirmation that we are processing your personal data and to access that data.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">What you can request:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• A copy of all personal data we hold about you</li>
                    <li>• Information about how we use your data</li>
                    <li>• Details about data retention periods</li>
                    <li>• Information about third-party data sharing</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Right to Data Portability */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Download" size={20} className="text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Data Portability (Article 20)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to receive your personal data in a structured, commonly used, and machine-readable format.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">How to exercise this right:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                    <li>• Export your data as JSON or CSV from Settings</li>
                    <li>• Transfer your data to another service provider</li>
                    <li>• Receive data in a machine-readable format</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/settings-and-privacy')}
                    iconName="Download"
                  >
                    Go to Data Export
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Right to Rectification */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Edit" size={20} className="text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Rectification (Article 16)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to have inaccurate personal data corrected or completed if it is incomplete.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">How to correct your data:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                    <li>• Update your profile information in Settings</li>
                    <li>• Edit symptom entries and health records</li>
                    <li>• Contact support for assistance with corrections</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/settings-and-privacy')}
                    iconName="Settings"
                  >
                    Go to Settings
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Right to Erasure */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Erasure ("Right to be Forgotten") (Article 17)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to request the deletion of your personal data under certain circumstances.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">When this applies:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                    <li>• Data is no longer necessary for its original purpose</li>
                    <li>• You withdraw consent and no other legal basis exists</li>
                    <li>• You object to processing and no overriding grounds exist</li>
                    <li>• Data has been unlawfully processed</li>
                  </ul>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Note:</strong> Account deletion includes a 30-day recovery window. After this period, all data is permanently deleted.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/settings-and-privacy')}
                    iconName="Trash2"
                  >
                    Manage Account Deletion
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Right to Restriction */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Pause" size={20} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Restriction of Processing (Article 18)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to request that we limit how we use your personal data in certain situations.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">When you can request restriction:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• You contest the accuracy of your personal data</li>
                    <li>• Processing is unlawful but you don't want erasure</li>
                    <li>• We no longer need the data but you need it for legal claims</li>
                    <li>• You've objected to processing pending verification</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Right to Object */}
          <section className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={20} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Right to Object (Article 21)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have the right to object to certain types of processing, including direct marketing and profiling.
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground font-medium mb-2">What you can object to:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                    <li>• Processing based on legitimate interests</li>
                    <li>• Direct marketing communications</li>
                    <li>• Profiling for marketing purposes</li>
                    <li>• Processing for research or statistical purposes</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/settings-and-privacy')}
                    iconName="Settings"
                  >
                    Manage Communication Preferences
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* How to Exercise Your Rights */}
          <section className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Mail" size={20} className="text-primary" />
              How to Exercise Your Rights
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              To exercise any of these rights, please contact us using one of the following methods:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Icon name="Mail" size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Email</p>
                  <a href="mailto:privacy@mybodytoday.com" className="text-sm text-primary hover:underline">
                    privacy@mybodytoday.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="MessageSquare" size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Support Portal</p>
                  <button
                    onClick={() => navigate('/help-and-support')}
                    className="text-sm text-primary hover:underline"
                  >
                    Contact Support Team
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg p-4 mt-4">
              <p className="text-xs text-muted-foreground">
                <strong>Response Time:</strong> We will respond to your request within 30 days. If your request is complex, 
                we may extend this period by an additional 60 days and will inform you of the extension.
              </p>
            </div>
          </section>

          {/* Additional Information */}
          <section className="bg-card border border-border rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={20} className="text-primary" />
              Additional Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Supervisory Authority</p>
                <p className="text-sm text-muted-foreground">
                  If you believe we have not handled your request appropriately, you have the right to lodge a complaint 
                  with your local data protection supervisory authority.
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground mb-1">No Fee Required</p>
                <p className="text-sm text-muted-foreground">
                  Exercising your GDPR rights is free of charge. However, we may charge a reasonable fee if your request 
                  is clearly unfounded, repetitive, or excessive.
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Identity Verification</p>
                <p className="text-sm text-muted-foreground">
                  To protect your privacy, we may need to verify your identity before processing your request. This helps 
                  ensure that personal data is not disclosed to unauthorized parties.
                </p>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => navigate('/privacy-policy')}
              className="bg-card border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Icon name="FileText" size={20} className="text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Privacy Policy</p>
                  <p className="text-xs text-muted-foreground">Read our full privacy policy</p>
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
              </div>
            </button>
            <button
              onClick={() => navigate('/terms-and-conditions')}
              className="bg-card border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Icon name="FileText" size={20} className="text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Terms & Conditions</p>
                  <p className="text-xs text-muted-foreground">View our terms of service</p>
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
              </div>
            </button>
          </div>
        </main>
      </div>
    </>
  );
};

export default GDPRRights;